import { useEffect, useMemo, useState } from 'react';
import { BlockStack } from '@shopify/polaris';
import { getRequest } from '../../../../shared/components/functions';
import PeriodBreakdownTable from './PeriodBreakdownTable';
import YearMonthMatrix from './YearMonthMatrix';
import TopPartnersCard from './TopPartnersCard';
import OrderBacklogCard from './OrderBacklogCard';
import BusinessUnitCard from './BusinessUnitCard';
import BusinessUnitFilter, { businessUnitParam, useBusinessUnitFilter, useBusinessUnitName } from './BusinessUnitFilter';
import PeriodDrilldownModal from './PeriodDrilldownModal';
import { getYears, toNumber } from './analyticsHelpers';

const revenueColumns = [
  { key: 'invoice_count', label: 'Invoices', format: 'integer' },
  { key: 'total_excl', label: 'Invoiced (excl. VAT)', format: 'currency' },
  { key: 'credited_excl', label: 'Credit notes (excl. VAT)', format: 'currency' },
  { key: 'net_excl', label: 'Net revenue (excl. VAT)', format: 'currency' },
  { key: 'total_incl', label: 'Invoiced (incl. VAT)', format: 'currency' },
  { key: 'open_amount', label: 'Still open', format: 'currency' }
];

const baseDrilldownConfig = {
  titlePrefix: 'Revenue',
  endpoint: '/api/metrics/revenue-period-detail',
  entityLabel: 'customers',
  entityHeading: 'Customer',
  invoiceLinkBase: '/invoices',
  showCreditedColumn: true
};

export default function RevenueTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drillPeriod, setDrillPeriod] = useState(null);
  const [businessUnitId, setBusinessUnitId] = useBusinessUnitFilter();
  const unitName = useBusinessUnitName(businessUnitId);

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      const result = await getRequest(`/api/metrics/revenue-table${businessUnitParam(businessUnitId, true)}`);
      if (cancelled) return;
      const rows = Array.isArray(result) ? result : [];
      setData(
        rows.map((row) => ({
          ...row,
          net_excl: toNumber(row.total_excl) - toNumber(row.credited_excl)
        }))
      );
      setLoading(false);
    }
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [businessUnitId]);

  const years = useMemo(() => getYears(data), [data]);
  const titleSuffix = unitName ? ` — ${unitName}` : '';
  const drilldownConfig = useMemo(() => ({ ...baseDrilldownConfig, titlePrefix: `Revenue${titleSuffix}` }), [titleSuffix]);

  return (
    <BlockStack gap="400">
      <BusinessUnitFilter value={businessUnitId} onChange={setBusinessUnitId} note="The order backlog card is hidden in this view because sales orders are not tagged with a unit." />
      <PeriodBreakdownTable
        title={`Outgoing invoices (revenue)${titleSuffix}`}
        subtitle="Customer invoicing per period. Excl. VAT comes from invoice lines (same status filter as the gross margin graph), incl. VAT from the invoice totals. Net revenue = invoiced excl. VAT minus outgoing credit notes. Click a period to see who it came from."
        rows={data}
        columns={revenueColumns}
        netKey="net_excl"
        netLabel="net revenue"
        loading={loading}
        onPeriodClick={setDrillPeriod}
      />
      <YearMonthMatrix
        title={`Revenue heatmap${titleSuffix}`}
        subtitle="Net revenue (excl. VAT) across all years — darker is stronger. Switch the view for coarser periods, or turn on cumulative to see the running year total: the bottom row then shows how much revenue a normal year has at that point."
        rows={data}
        valueKey="net_excl"
        colorRgb="46, 125, 50"
        loading={loading}
        onPeriodClick={setDrillPeriod}
      />
      {!businessUnitId && <OrderBacklogCard />}
      {!businessUnitId && <BusinessUnitCard />}
      {years.length > 0 && (
        <TopPartnersCard
          title={`Top customers${titleSuffix}`}
          subtitle="Who your revenue depends on, incl. concentration risk. Values incl. VAT, based on invoice date."
          endpoint="/api/metrics/top-customers"
          idKey="customer_id"
          nameKey="customer_name"
          entityLabel="customers"
          years={years}
          businessUnitId={businessUnitId}
        />
      )}
      <PeriodDrilldownModal period={drillPeriod} onClose={() => setDrillPeriod(null)} config={drilldownConfig} businessUnitId={businessUnitId} />
    </BlockStack>
  );
}
